'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateDonationRequestModeEnum, Project, Reward, Subproject } from '@/app/api';
import { Country, DonationFormType } from '@/app/enums';

import { RadioGroupOption } from '@/app/components/FormRadioGroup/FormRadioGroup';

import GoodHabitDonationForm, {
  GoodHabitDonationFormValuesAuthenticatedUser,
  GoodHabitDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/GoodHabitDonationForm/GoodHabitDonationForm';

import LongTermDonationForm, {
  LongTermDonationFormValuesAuthenticatedUser,
  LongTermDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/LongTermDonationForm/LongTermDonationForm';

import SingleDonationForm, {
  SingleDonationFormValuesAuthenticatedUser,
  SingleDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/SingleDonationForm/SingleDonationForm';

import CommercialProjectDonationForm, {
  CommercialProjectDonationFormValues,
} from '@/app/components/DonationForm/CommercialProjectDonationForm/CommercialProjectDonationForm';
import { useProjectData } from '@/app/providers/ProjectProvider';
import { Donation } from '@/src/api/models';

interface SingleDonationFormProps {
  formType: DonationFormType.SINGLE_AUTHENTICATED_USER | DonationFormType.SINGLE_UNAUTHENTICATED_USER;
  project: Project;
  subproject?: Subproject;
  initialValues?: Partial<SingleDonationFormValuesAuthenticatedUser | SingleDonationFormValuesUnauthenticatedUser>;
  shouldDisplayPrivacyConsentCheckbox?: boolean;
  amountOptions: RadioGroupOption<number>[];
}

interface LongTermDonationFormProps {
  formType: DonationFormType.LONG_TERM_AUTHENTICATED_USER | DonationFormType.LONG_TERM_UNAUTHENTICATED_USER;
  project: Project;
  subproject?: Subproject;
  initialValues?: Partial<LongTermDonationFormValuesAuthenticatedUser | LongTermDonationFormValuesUnauthenticatedUser>;
  shouldDisplayPrivacyConsentCheckbox?: boolean;
  amountOptions: RadioGroupOption<number>[];
}

interface GoodHabitDonationFormProps {
  formType: DonationFormType.GOOD_HABIT_AUTHENTICATED_USER | DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER;
  project: Project;
  subproject?: Subproject;
  regularDonationFundsAvailable?: Donation[];
  initialValues?: Partial<
    GoodHabitDonationFormValuesAuthenticatedUser | GoodHabitDonationFormValuesUnauthenticatedUser
  >;
  isCategoriesSelectVisible?: boolean;
  shouldDisplayPrivacyConsentCheckbox?: boolean;
  amountOptions: RadioGroupOption<number>[];
}

interface CommercialProjectFormProps {
  formType: DonationFormType.COMMERCIAL_PROJECT;
  project: Project;
  subproject?: Subproject;
  initialValues?: Partial<CommercialProjectDonationFormValues>;
  shouldDisplayPrivacyConsentCheckbox?: boolean;
  rewards?: Reward[];
}

const DonationForm = (
  props: SingleDonationFormProps | LongTermDonationFormProps | GoodHabitDonationFormProps | CommercialProjectFormProps,
) => {
  const router = useRouter();
  const { subProject } = useProjectData();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const countrySelectOptions = useMemo(() => {
    // TODO add to env variables?
    const highlightedCountries = [Country.CZECH_REPUBLIC, Country.SLOVAKIA];

    const allCountryOptions = Object.values(Country).map((countryCode) => ({
      value: countryCode,
      label: t(`enums.countries.${countryCode}`),
    }));

    const highlightedOptions = allCountryOptions
      .filter((option) => highlightedCountries.includes(option.value))
      .map((option) => ({ ...option, highlighted: true }));

    const remainingOptions = allCountryOptions.filter((option) => !highlightedCountries.includes(option.value));

    return [{ value: undefined, label: '', highlighted: true }, ...highlightedOptions, ...remainingOptions];
  }, [t]);

  const donationModeOptions = useMemo(() => {
    return [
      { value: CreateDonationRequestModeEnum.Single, label: t('components.DonationForm.oneTimeDonation') },
      { value: CreateDonationRequestModeEnum.LongTerm, label: t('components.DonationForm.regularDonation') },
    ];
  }, [t]);

  const handleCloseButtonClick = useCallback(() => {
    const languageQuery = language === 'en' ? `lang=${language}` : '';

    const searchPrams = languageQuery ? `?${languageQuery}` : '';

    if (subProject) {
      window.location.href = `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${props.project.slug}/${subProject.slug}${searchPrams}`;
      return;
    }

    window.location.href = `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${props.project.slug}${searchPrams}`;
  }, [props.project, router, language, subProject]);
  switch (props.formType) {
    case DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER:
      return (
        <GoodHabitDonationForm<GoodHabitDonationFormValuesUnauthenticatedUser>
          project={props.project}
          subproject={props.subproject}
          countrySelectOptions={countrySelectOptions}
          isCategoriesSelectVisible={props.isCategoriesSelectVisible}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as GoodHabitDonationFormValuesUnauthenticatedUser}
        />
      );

    case DonationFormType.GOOD_HABIT_AUTHENTICATED_USER:
      return (
        <GoodHabitDonationForm<GoodHabitDonationFormValuesAuthenticatedUser>
          project={props.project}
          subproject={props.subproject}
          countrySelectOptions={countrySelectOptions}
          isCategoriesSelectVisible={props.isCategoriesSelectVisible}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as GoodHabitDonationFormValuesAuthenticatedUser}
        />
      );

    case DonationFormType.LONG_TERM_UNAUTHENTICATED_USER:
      return (
        <LongTermDonationForm<LongTermDonationFormValuesUnauthenticatedUser>
          shouldDisplayPrivacyConsentCheckbox={props.shouldDisplayPrivacyConsentCheckbox}
          countrySelectOptions={countrySelectOptions}
          amountOptions={props.amountOptions}
          donationModeOptions={donationModeOptions}
          project={props.project}
          subproject={props.subproject}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as LongTermDonationFormValuesUnauthenticatedUser}
        />
      );

    case DonationFormType.LONG_TERM_AUTHENTICATED_USER:
      return (
        <LongTermDonationForm<LongTermDonationFormValuesAuthenticatedUser>
          shouldDisplayPrivacyConsentCheckbox={props.shouldDisplayPrivacyConsentCheckbox}
          countrySelectOptions={countrySelectOptions}
          amountOptions={props.amountOptions}
          donationModeOptions={donationModeOptions}
          project={props.project}
          subproject={props.subproject}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as LongTermDonationFormValuesAuthenticatedUser}
        />
      );

    case DonationFormType.COMMERCIAL_PROJECT:
      return (
        <CommercialProjectDonationForm
          shouldDisplayPrivacyConsentCheckbox={props.shouldDisplayPrivacyConsentCheckbox}
          countrySelectOptions={countrySelectOptions}
          project={props.project}
          subproject={props.subproject}
          rewards={props.rewards}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as CommercialProjectDonationFormValues}
        />
      );

    case DonationFormType.SINGLE_UNAUTHENTICATED_USER:
      return (
        <SingleDonationForm<SingleDonationFormValuesUnauthenticatedUser>
          shouldDisplayPrivacyConsentCheckbox={props.shouldDisplayPrivacyConsentCheckbox}
          countrySelectOptions={countrySelectOptions}
          amountOptions={props.amountOptions}
          project={props.project}
          subproject={props.subproject}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as SingleDonationFormValuesUnauthenticatedUser}
        />
      );

    case DonationFormType.SINGLE_AUTHENTICATED_USER:
    default:
      return (
        <SingleDonationForm<SingleDonationFormValuesAuthenticatedUser>
          shouldDisplayPrivacyConsentCheckbox={props.shouldDisplayPrivacyConsentCheckbox}
          countrySelectOptions={countrySelectOptions}
          amountOptions={props.amountOptions}
          project={props.project}
          subproject={props.subproject}
          onCloseButtonClick={handleCloseButtonClick}
          initialValues={props.initialValues as SingleDonationFormValuesAuthenticatedUser}
        />
      );
  }
};

export default DonationForm;
